import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ריבוע צבעוני לאייקון — זהות חזותית לכרטיס/מדד. האייקון דקורטיבי (aria-hidden);
 * המשמעות תמיד בטקסט שלצידו. `solid` נושא את fg-on-accent: לבן בבהיר, כהה בכהה —
 * בשני המצבים ≥4.5:1 מול כל אחד מהגוונים (ראו theme.css).
 */
const chipVariants = cva('grid shrink-0 place-items-center', {
  variants: {
    tone: {
      accent: '',
      success: '',
      warning: '',
      danger: '',
      info: '',
      violet: '',
      teal: '',
      orange: '',
      pink: '',
    },
    variant: { soft: '', solid: 'text-fg-on-accent shadow-xs' },
    size: {
      sm: 'size-7 rounded-(--radius-sm) [&_svg]:size-3.5',
      md: 'size-9 rounded-(--radius-md) [&_svg]:size-4',
      lg: 'size-11 rounded-(--radius-lg) [&_svg]:size-5',
    },
  },
  compoundVariants: [
    { variant: 'soft', tone: 'accent', className: 'bg-accent-subtle text-accent ring-1 ring-inset ring-accent-border/60' },
    { variant: 'soft', tone: 'success', className: 'bg-success-subtle text-success ring-1 ring-inset ring-success-border/60' },
    { variant: 'soft', tone: 'warning', className: 'bg-warning-subtle text-warning ring-1 ring-inset ring-warning-border/60' },
    { variant: 'soft', tone: 'danger', className: 'bg-danger-subtle text-danger ring-1 ring-inset ring-danger-border/60' },
    { variant: 'soft', tone: 'info', className: 'bg-info-subtle text-info ring-1 ring-inset ring-info-border/60' },
    { variant: 'soft', tone: 'violet', className: 'bg-violet-subtle text-violet ring-1 ring-inset ring-violet-border/60' },
    { variant: 'soft', tone: 'teal', className: 'bg-teal-subtle text-teal ring-1 ring-inset ring-teal-border/60' },
    { variant: 'soft', tone: 'orange', className: 'bg-orange-subtle text-orange ring-1 ring-inset ring-orange-border/60' },
    { variant: 'soft', tone: 'pink', className: 'bg-pink-subtle text-pink ring-1 ring-inset ring-pink-border/60' },
    { variant: 'solid', tone: 'accent', className: 'bg-accent' },
    { variant: 'solid', tone: 'success', className: 'bg-success' },
    { variant: 'solid', tone: 'warning', className: 'bg-warning' },
    { variant: 'solid', tone: 'danger', className: 'bg-danger' },
    { variant: 'solid', tone: 'info', className: 'bg-info' },
    { variant: 'solid', tone: 'violet', className: 'bg-violet' },
    { variant: 'solid', tone: 'teal', className: 'bg-teal' },
    { variant: 'solid', tone: 'orange', className: 'bg-orange' },
    { variant: 'solid', tone: 'pink', className: 'bg-pink' },
  ],
  defaultVariants: { tone: 'accent', variant: 'soft', size: 'md' },
});

export type Tone = NonNullable<VariantProps<typeof chipVariants>['tone']>;

export interface IconChipProps extends VariantProps<typeof chipVariants> {
  icon: LucideIcon;
  className?: string;
}

export function IconChip({ icon: Icon, tone, variant, size, className }: IconChipProps) {
  return (
    <span className={cn(chipVariants({ tone, variant, size }), className)} aria-hidden>
      <Icon aria-hidden />
    </span>
  );
}
